import { useCallback, useEffect, useRef, useState } from 'react';
import { getAllApps } from '../../../services/app/app-service';
import { listDataSources } from '../../../services/knowledge';
import {
    getAgentActions,
    getPromptConfigMcpServers,
    getPromptConfigSkills,
    getPromptConfigTools,
} from '../../../services/prompt-ai';
import { isPlaybookRecord } from '../../playbooks/model/playbook-model';
import { RESOURCE_CHANGED_EVENT } from './use-copilot';

const SUB_AGENT_TOOL_TYPE = 'sub_agent';
const REFRESH_DEBOUNCE_MS = 700;

const asList = value => (Array.isArray(value) ? value.filter(item => item && typeof item === 'object') : []);
const settled = (result, fallback) => (result.status === 'fulfilled' ? result.value : fallback);
const idOf = item => String(item.id ?? item.toolId ?? item.name ?? '');
const nameOf = item => String(item.name || item.title || item.id || 'Untitled');

/** Each resource as `{ id, name, meta, href }`; href is relative to the app base path. */
async function loadAppResources(appId) {
    const [tools, skills, mcpServers, actions, dataSources] = await Promise.allSettled([
        getPromptConfigTools(appId),
        getPromptConfigSkills(appId),
        getPromptConfigMcpServers(appId),
        getAgentActions(appId),
        listDataSources(appId),
    ]);

    const toolList = asList(settled(tools, []));
    const actionList = asList(settled(actions, {})?.data);
    const sources = settled(dataSources, null);

    return {
        subAgents: toolList.filter(t => t.type === SUB_AGENT_TOOL_TYPE)
            .map(t => ({ id: idOf(t), name: nameOf(t), meta: 'Sub-agent', href: `/sub-agents/${idOf(t)}` })),
        tools: toolList.filter(t => t.type !== SUB_AGENT_TOOL_TYPE)
            .map(t => ({ id: idOf(t), name: nameOf(t), meta: t.type ? String(t.type).replace(/_/g, ' ') : '', href: '/tools' })),
        skills: asList(settled(skills, []))
            .map(s => ({ id: idOf(s), name: nameOf(s), href: '/skills' })),
        mcpServers: asList(settled(mcpServers, []))
            .map(m => ({ id: idOf(m), name: nameOf(m), href: '/mcp-servers' })),
        playbooks: actionList.filter(isPlaybookRecord)
            .map(p => ({ id: idOf(p), name: nameOf(p), href: `/playbooks/${idOf(p)}` })),
        workflows: actionList.filter(a => !isPlaybookRecord(a))
            .map(w => ({ id: idOf(w), name: nameOf(w), href: `/journey/${idOf(w)}` })),
        knowledge: (sources?.status === 200 ? asList(sources.response?.data) : [])
            .map(k => ({
                id: String(k.dataSourceId ?? k.id ?? ''),
                name: String(k.name || k.dataSourceName || k.dataSourceId || 'Source'),
                meta: [k.dataSourceType, k.dataSourceType === 'file' ? k.fileType : k.dbType].filter(Boolean).join(' · '),
                status: k.modelStatus ? String(k.modelStatus) : '',
                href: '/contents',
            })),
    };
}

/**
 * What the right panel shows: the application's resources (app scope), or the account's
 * applications (account scope). Reloads, debounced, whenever the copilot reports a change.
 */
export default function useCopilotResources({ appId, isAccountScope }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const requestRef = useRef(0);

    const load = useCallback(async ({ quiet = false } = {}) => {
        if (!appId && !isAccountScope) return;
        const request = ++requestRef.current;
        if (!quiet) setLoading(true);
        setError(null);
        try {
            const next = appId
                ? await loadAppResources(appId)
                : { applications: asList(await getAllApps()).map(app => ({ id: String(app.appId), name: nameOf(app), meta: app.description || '', appMode: app.appMode })) };
            if (request === requestRef.current) setData(next);
        } catch (_) {
            if (request === requestRef.current) setError("Couldn't load resources.");
        } finally {
            if (request === requestRef.current) setLoading(false);
        }
    }, [appId, isAccountScope]);

    useEffect(() => {
        setData(null);
        load();
    }, [load]);

    useEffect(() => {
        let timer = null;
        const onChanged = event => {
            const changedApp = event?.detail?.appId;
            if (appId && changedApp && changedApp !== appId) return;
            clearTimeout(timer);
            timer = setTimeout(() => load({ quiet: true }), REFRESH_DEBOUNCE_MS);
        };
        window.addEventListener(RESOURCE_CHANGED_EVENT, onChanged);
        return () => {
            clearTimeout(timer);
            window.removeEventListener(RESOURCE_CHANGED_EVENT, onChanged);
        };
    }, [appId, load]);

    return { data, loading, error, refresh: load };
}
